import dayjs from 'dayjs'
import { BILLING_CYCLE, ipRegex } from "./constants";

export const debounce = (fn: Function, ms = 300) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};


export function formatPhoneNumber(phoneNumber: string) {
  if (!phoneNumber) return phoneNumber;
  const cleanNum = phoneNumber.toString().replace(/\D/g, '');
  const cleanNumLength = cleanNum.length;
  if (cleanNumLength < 4) return cleanNum;
  if (cleanNumLength < 7) {
    return `(${cleanNum.slice(0, 3)}) ${cleanNum.slice(3)}`;
  }

  return `(${cleanNum.slice(0, 3)}) ${cleanNum.slice(3, 6)}-${cleanNum.slice(6, 10)}`
}

export function getImageSize(file: File) {
  const reader = new FileReader();

  reader.readAsDataURL(file);

  const promise = new Promise((resolve, reject) => {
    reader.onload = function (e) {
      const image = new Image();

      image.src = e.target?.result as string;

      image.onload = function () {
        const height = image.height;
        const width = image.width;

        resolve({ width, height });
      }

      image.onerror = reject;
    }
  })

  return promise as Promise<{ height: number, width: number }>;
}

export function compareTime(a: any, b: any) {
  const timeA = new Date();
  timeA.setHours(a.split(":")[0], a.split(":")[1]);

  const timeB = new Date();
  timeB.setHours(b.split(":")[0], b.split(":")[1]);

  return timeA > timeB
}

export function validateIP(ip: string) {
  if (!ip) return

  return ipRegex.test(ip);
}

export function capitaliseStr(name: string | undefined) {
  if (!name) return;
  const hasWhiteSpace = name.includes(" ");
  if (hasWhiteSpace) {
    const [first, last] = name.split(" ");
    const firstName = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
    const lastName = last.charAt(0).toUpperCase() + last.slice(1).toLowerCase();

    return `${firstName} ${lastName}`;
  }

  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

export function formatInputToNumber(value: string) {
  if (!value) return value;
  if (value.charAt(0) === "0") {
    value = value.substring(1);
  }
  const cleanNum = value.toString().replace(/\D/g, '');
  return cleanNum
}

export function trimValue(value: string) {
  if (!value) return value;
  return value.trim();
}


export const toDataURL = (url: string) => fetch(url)
  .then(response => response.blob())
  .then(blob => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  }))

export const fileToDataURL = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export function dataURLtoFile(dataurl: string) {
  const fileName = "loginImage.png"
  const arr = dataurl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1]
  const bstr = atob(arr[1])
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, { type: mime });
}

export function createArray(N: number) {
  return Array.from({ length: N }, (_, index) => ({ label: (index + 1).toString(), value: (index + 1).toString() }));
}


export function convertTimeToMS(time: string) {
  return Number(time.split(':')[0]) * 60 * 60 * 1000 + Number(time.split(':')[1]) * 60 * 1000;
}


export function isNotValidDayLightTime({ startTime, endTime, selectedTime }: { startTime: string, endTime: string, selectedTime: string }) {
  const startTimeMS = convertTimeToMS(startTime);
  const endTimeMS = convertTimeToMS(endTime);
  const selectedTimeMS = convertTimeToMS(selectedTime);
  return selectedTimeMS >= startTimeMS && selectedTimeMS <= endTimeMS;
}

export function currentUserLoginPath(clientId: string): string {
  const encryptedClientId = btoa(clientId);
  return clientId && clientId !== "-1" ? `/?token=${encryptedClientId}` : "/"
}

export const validateParcelNumber = (format: string, parcelNumber: string) => {
  if (!parcelNumber || !format) return true;
  const parcelNumFormatArr = format.split('-');
  const parcelNumArr = parcelNumber.split('-');

  for (let i = 0; i < parcelNumFormatArr.length; i++) {
    const parcelNumArrPart = parcelNumArr[i];
    if (isNaN(+parcelNumArrPart)) {
      return false;
    }

    if (parcelNumArrPart && parcelNumArrPart.length !== parcelNumFormatArr[i].length) {
      return false;
    }
  }

  return true;
}

export const formatName = (data: any) => {
  if (!data) return '';
  const { firstName, lastName } = data;
  return `${firstName} ${lastName}`
};

function formatCountry(name: string) {
  if (!name) return '';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}


export const formatAddress = (data: any, showNumberOfUnits?: boolean) => {
  if (!data) return '';

  const shouldShowNumberOfUnits = showNumberOfUnits ?? false;

  const {
    addressLine1,
    addressLine2,
    country,
    state: addressState,
    city,
    zipCode,
    numberOfUnits,
  } = data;

  return `
    ${addressLine1 ? addressLine1 + '<br/>' : ''}
    ${addressLine2 ? addressLine2 + '<br/>' : ''}
    ${city ? city + ', ' : ''}
    ${addressState ? addressState + ' ' : ''}
    ${zipCode ? zipCode + '<br/>' : ''}
    ${country !== "UNITED STATES" ? formatCountry(country) + '<br/>' : ''}
    ${numberOfUnits && shouldShowNumberOfUnits
      ? `<br/> Number of Units ${numberOfUnits}`
      : ''
    }
  `;
};

export const formatAddressDropDown = (
  data: any,
  showNumberOfUnits?: boolean,
) => {
  if (!data) return "";

  const shouldShowNumberOfUnits = showNumberOfUnits ?? false;

  const {
    addressLine1,
    addressLine2,
    country,
    state: addressState,
    city,
    zipCode,
    numberOfUnits,
  } = data;

  const parts: string[] = [];

  if (addressLine1) parts.push(addressLine1);
  if (addressLine2) parts.push(addressLine2);
  if (city) parts.push(city);
  if (addressState) parts.push(addressState);
  if (zipCode) parts.push(zipCode);

  if (country && country !== "UNITED STATES") {
    parts.push(formatCountry(country));
  }

  if (numberOfUnits && shouldShowNumberOfUnits) {
    parts.push(`Number of Units ${numberOfUnits}`);
  }

  return parts.join(", ");
};


export const downloadFile = (fileName: string, url: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute(
    'download',
    fileName,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export const getDownloadFileContent = (response: any) => {
  if (!response) return { fileName: '', url: '' };
  const fileName = response?.headers['file-name'] || response?.headers['content-disposition']?.split('filename=')[1];
  const { data } = response;
  const fileBlob = new Blob([data]);
  return { fileName, url: URL.createObjectURL(fileBlob) }
}

/**
 * Extract PDF blob data from API response for printing
 * @param response - API response containing PDF byte stream
 * @returns Object containing fileName and blob data for printing
 */
export const getPrintFileContent = (response: any) => {
  if (!response) return { fileName: '', blob: null };
  const fileName = response?.headers['file-name'] || response?.headers['content-disposition']?.split('filename=')[1];
  const { data } = response;
  const fileBlob = new Blob([data], { type: 'application/pdf' });
  return { fileName, blob: fileBlob }
}

export function formatOption(option: any[]) {
  if (!Array.isArray(option)) return [];
  return option.map(({ key, value, ...rest }) => ({ label: value, value: key, ...rest }));
}


export const currentDate = (curr?: number) => {
  const date = curr ? new Date((new Date()).setDate(curr)) : new Date();
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

export function findLastDuplicate(arr: string[] | Record<string, any>[], valueToBeChecked?: string) {
  const duplicates = [];
  for (let i = 0; i < arr.length; i++) {
    for (let j = i + 1; j < arr.length; j++) {
      if (typeof arr[i] === 'string' && arr[j] === 'string') {
        if (arr[i] === arr[j]) {
          duplicates.push(j);
        }
      }

      if (typeof arr[i] === 'object' && typeof arr[j] === 'object') {
        if (arr[i][`${valueToBeChecked}` as keyof typeof arr[typeof i]] === arr[j][`${valueToBeChecked}` as keyof typeof arr[typeof j]]) {
          duplicates.push(j);
        }
      }
    }
  }
  return duplicates;
}

export const validateMergeFields = (arr: string[], value: string) => {
  if (arr.length > 0) {
    for (let i = 0; i < arr.length; i++) {
      value = value?.replaceAll(arr[i], "")
    }
    if (value?.includes("{") || value?.includes("}")) {
      return false;
    }
    return true;
  }

  return false;

}

export const sortColumnValues = (a: string | number, b: string | number) => {
  let sort: number;
  if (a > b) {
    sort = 1;
  } else if (b > a) {
    sort = -1
  } else {
    sort = 0;
  }

  return sort;
}

export const validate = (value: string, validUpToPoints: number = 3) => {
  return value.indexOf(".") >= 0 ? value.slice(0, value.indexOf(".") + validUpToPoints) : value;
}

export const checkObjectsAreEqual = (obj1: any, obj2: any) => {
  return Object

    // Get all the keys in array
    .keys(obj2)
    .every(val => obj1.hasOwnProperty(val)
      && obj1[val] === obj2[val])
}


export const getNextDate = (savedDate: string | undefined) => {
  if (!savedDate) return new Date();
  const date = new Date(savedDate)
  let month = date.getMonth();
  month++;
  date.setMonth(month);

  return date;
}

export const formatArrOfStrValue = (value: string) => {
  if (!value) return '';
  if (value.length > 12) {
    return value.substring(0, 12) + '...'
  }

  return value;

}


export const formatAmountLabel = (label: string, billingCycleLength: string) => {
  return label.replace("{BILLING_CYCLE}", BILLING_CYCLE?.[billingCycleLength as keyof typeof BILLING_CYCLE]?.labelDisplayName || "Monthly")
}

export const getBillingCycleLength = (billingCycleLength: string) => {
  return BILLING_CYCLE?.[billingCycleLength as keyof typeof BILLING_CYCLE]?.billingCycle ?? null
}

export function areAllObjectsFilledBeforeLast(arr: any[] | null, keyToCheck: string) {
  if (!arr) {
    return;
  }

  if (arr.length <= 0) {
    return true;
  }
  return arr.slice(0, -1).every(item => item[keyToCheck])

}

export function areAllObjectsFilled(arr: any[] | null, keyToCheck: string) {
  if (!arr) {
    return;
  }

  if (arr.length <= 0) {
    return true;
  }
  return arr.every(item => item[keyToCheck])
}

export function isTruthy(value: unknown): boolean {
  return value === 0 || Boolean(value);
}



export const normalize = (val?: string | null) => val?.trim() || '';

export const formatCurrency = (value: number | string) =>
  !isNaN(Number(value)) ? `$${Number(value)?.toFixed(2)}` : value;

export function formatLocalizedCurrency(
  amount: any,
  options?: {
    currency?: string;
    locale?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  const {
    currency = 'USD',
    locale = 'en-US',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options || {};

  if (amount === '' || amount === null || amount === undefined) return ''

  const parsed = typeof amount === 'number' ? amount : Number(amount);
  if (isNaN(parsed)) return String(amount);

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(parsed);
}

export const isMoreThanOneYear = (date1: dayjs.Dayjs, date2: dayjs.Dayjs) => {
  return Math.abs(date1.diff(date2, 'year', true)) > 1;
};

export const toNumberOrNull = (val: any) => {
  if (val === "" || val === null || val === undefined) return null;
  const num = Number(val);
  return isNaN(num) ? null : num;
};

export const normalizeDate = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value[0] ?? "";
  return "";
};



